import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3,
  CalendarDays,
  CheckCheck,
  ClipboardCheck,
  Copy,
  FileDown,
  FileText,
  Grid3x3,
  HeartPulse,
  ImageDown,
  ListChecks,
  LoaderCircle,
  MessageCircle,
  Plus,
  Printer,
  Receipt,
  Shirt,
  Trash2,
  UsersRound,
  WalletCards,
  Waves,
  X
} from 'lucide-react';
import { Colonia } from '../../types/colonia';
import AdminTopbar from './AdminTopbar';
import { getAvailableAdminPeriods, getCurrentAdminPeriod, getInscricaoDate, getInscricaoYear, useAdminInscricoesData } from './adminData';
import { generateShirtSummaryImage } from './shirtSummaryImage';
import { generateShirtStockImage } from './shirtStockImage';
import { ReportItem, generateReportImage } from './reportImage';
import { Gasto, categoriasGasto, createGasto, deleteGasto, totalDoGasto, useGastos } from './gastosData';
import { baixarPdfGastos } from './gastosPdf';
import { TURMAS } from '../formularioParts/utils';
import { CarteirinhasPanel, CartelasBingoPanel } from './AdminFloatingMenu';
import {
  CompraEvento,
  ItemEvento,
  fetchComprasEvento,
  fetchItensEvento,
  salvarItensEvento,
  verificarPagamentosEvento
} from '../eventoAdicionalData';
import { TermoAutorizacaoRegistro, fetchTermosAutorizacao } from '../termoAutorizacaoData';
import { ChamadaDia, fetchTodasChamadas } from './chamadaData';
import { RelatorioFinalDados, baixarRelatorioFinalPdf } from './relatorioFinalPdf';
import {
  ReportEmptyState,
  ReportHeader,
  ReportMetricItem,
  ReportMetrics,
  ReportNavigationItem,
  ReportPanel,
  ReportSection,
  ReportsNavigation,
  ReportsOverview
} from './reports/ReportUI';
import './AdminPanel.css';
import './reports/ReportUI.css';

type ReportTab =
  | 'turmas'
  | 'camisas'
  | 'natacao'
  | 'saude'
  | 'adicionais'
  | 'aniversariantes'
  | 'gastos'
  | 'lista'
  | 'carteirinhas'
  | 'termo'
  | 'final';

const turmasPadrao = TURMAS;
const tamanhosPadrao = ['04', '06', '08', '10', '12', '14', '16', 'P', 'M', 'G', 'GG'];

/** Camisas encomendadas — serve de ponto de partida e pode ser editado por período. */
const estoqueEncomendado: Record<string, number> = {
  '04': 5,
  '06': 15,
  '08': 19,
  '10': 17,
  '12': 5,
  '14': 7,
  '16': 2,
  P: 6,
  M: 10
};

const estoqueStorageKey = (ano: string) => `colonia:estoque-camisas:${ano}`;

const paraItemRelatorio = (item: Colonia, detalhe?: (item: Colonia) => string | undefined): ReportItem => ({
  nome: item.nomeCompleto,
  detalhe: (detalhe ? detalhe(item) : item.turma) || 'Sem turma'
});

const formatarDataCurta = (valor: string) => {
  if (!valor) return '';
  const [ano, mes, dia] = valor.split('-');
  return dia && mes ? `${dia}/${mes}${ano ? `/${ano}` : ''}` : valor;
};

const lerEstoqueSalvo = (ano: string): Record<string, number> => {
  try {
    const salvo = window.localStorage.getItem(estoqueStorageKey(ano));
    if (!salvo) return { ...estoqueEncomendado };
    const parsed = JSON.parse(salvo) as Record<string, unknown>;
    return Object.entries(parsed).reduce<Record<string, number>>((acc, [tamanho, valor]) => {
      const numero = Number(valor);
      if (Number.isFinite(numero) && numero >= 0) acc[tamanho] = numero;
      return acc;
    }, {});
  } catch {
    return { ...estoqueEncomendado };
  }
};

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

const getMonthDay = (dateValue: string) => {
  if (!dateValue) return '';
  const [, month, day] = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})/) || [];
  return month && day ? `${month}-${day}` : '';
};

const RelatoriosColonia: React.FC = () => {
  const navigate = useNavigate();
  const { inscricoes, loading, error } = useAdminInscricoesData();
  const [selectedYear, setSelectedYear] = useState(getCurrentAdminPeriod);
  const [activeTab, setActiveTab] = useState<ReportTab>('turmas');
  const [estoque, setEstoque] = useState<Record<string, number>>(() => lerEstoqueSalvo(getCurrentAdminPeriod()));
  const { gastos, loading: gastosLoading, error: gastosError, setGastos } = useGastos(selectedYear);
  const [novoGasto, setNovoGasto] = useState({
    descricao: '',
    categoria: categoriasGasto[0],
    quantidade: '1',
    valorUnitario: '',
    data: ''
  });
  const [gastoSalvando, setGastoSalvando] = useState(false);
  const [gastoErro, setGastoErro] = useState('');

  const [itensEvento, setItensEvento] = useState<ItemEvento[]>([]);
  const [itensEventoCarregando, setItensEventoCarregando] = useState(true);
  const [itensEventoErro, setItensEventoErro] = useState('');
  const [itensEventoSalvando, setItensEventoSalvando] = useState(false);
  const [itensEventoSalvo, setItensEventoSalvo] = useState(false);

  const [comprasEvento, setComprasEvento] = useState<CompraEvento[]>([]);
  const [comprasEventoCarregando, setComprasEventoCarregando] = useState(true);
  const [comprasEventoErro, setComprasEventoErro] = useState('');
  const [verificandoPagamentosEvento, setVerificandoPagamentosEvento] = useState(false);
  const [linkEventoCopiado, setLinkEventoCopiado] = useState(false);
  const [mostrarCartelasBingo, setMostrarCartelasBingo] = useState(false);
  const [termosAutorizacao, setTermosAutorizacao] = useState<TermoAutorizacaoRegistro[]>([]);
  const [termosCarregando, setTermosCarregando] = useState(true);
  const [termosErro, setTermosErro] = useState('');
  const [termoSelecionado, setTermoSelecionado] = useState<TermoAutorizacaoRegistro | null>(null);

  useEffect(() => {
    fetchTermosAutorizacao()
      .then(setTermosAutorizacao)
      .catch((error: Error) => setTermosErro(error.message))
      .finally(() => setTermosCarregando(false));
  }, []);

  const [chamadas, setChamadas] = useState<ChamadaDia[]>([]);
  const [chamadasCarregando, setChamadasCarregando] = useState(true);
  const [gerandoRelatorioFinal, setGerandoRelatorioFinal] = useState(false);

  useEffect(() => {
    fetchTodasChamadas()
      .then(setChamadas)
      .catch(() => setChamadas([]))
      .finally(() => setChamadasCarregando(false));
  }, []);

  const carregarComprasEvento = () => {
    setComprasEventoCarregando(true);
    fetchComprasEvento()
      .then((lista) => {
        setComprasEvento(lista);
        setComprasEventoErro('');
      })
      .catch((error: Error) => setComprasEventoErro(error.message))
      .finally(() => setComprasEventoCarregando(false));
  };

  useEffect(() => {
    fetchItensEvento()
      .then((lista) => {
        setItensEvento(lista);
        setItensEventoErro('');
      })
      .catch((error: Error) => setItensEventoErro(error.message))
      .finally(() => setItensEventoCarregando(false));
    carregarComprasEvento();
  }, []);

  const adicionarItemEvento = () => {
    setItensEvento((atual) => [...atual, { id: `item-${Date.now()}`, nome: '', valor: 0 }]);
  };

  const editarItemEvento = (id: string, patch: Partial<ItemEvento>) => {
    setItensEvento((atual) => atual.map((item) => (item.id === id ? { ...item, ...patch } : item)));
  };

  const removerItemEvento = (id: string) => {
    setItensEvento((atual) => atual.filter((item) => item.id !== id));
  };

  const salvarItensEventoHandler = async () => {
    setItensEventoSalvando(true);
    setItensEventoErro('');
    setItensEventoSalvo(false);
    try {
      const salvo = await salvarItensEvento(itensEvento);
      setItensEvento(salvo);
      setItensEventoSalvo(true);
      window.setTimeout(() => setItensEventoSalvo(false), 2500);
    } catch (error) {
      setItensEventoErro((error as Error).message);
    } finally {
      setItensEventoSalvando(false);
    }
  };

  const copiarLinkEvento = () => {
    navigator.clipboard.writeText(`${window.location.origin}/evento`);
    setLinkEventoCopiado(true);
    window.setTimeout(() => setLinkEventoCopiado(false), 2500);
  };

  const avisarPendenciaEventoWhatsapp = (compra: CompraEvento) => {
    const numeros = String(compra.telefoneResponsavel || '').replace(/\D/g, '');
    const numero = numeros.length === 10 || numeros.length === 11 ? `55${numeros}` : numeros;
    if (!numero) return;
    const mensagem = `Olá, ${compra.nomeResponsavel}! Notamos que o pagamento da inscrição de ${compra.nomeCrianca} no evento (Bingo/Cachorro-quente) ainda está pendente.${
      compra.invoiceUrl ? ` Você pode finalizar por aqui: ${compra.invoiceUrl}` : ''
    } Qualquer dúvida, estamos à disposição!`;
    window.open(`https://wa.me/${numero}?text=${encodeURIComponent(mensagem)}`, '_blank');
  };

  const verificarPagamentosEventoHandler = useCallback(async () => {
    if (verificandoPagamentosEvento) return;
    setVerificandoPagamentosEvento(true);
    setComprasEventoErro('');
    try {
      await verificarPagamentosEvento();
      carregarComprasEvento();
    } catch (error) {
      setComprasEventoErro((error as Error).message);
    } finally {
      setVerificandoPagamentosEvento(false);
    }
  }, [verificandoPagamentosEvento]);

  useEffect(() => {
    if (activeTab !== 'adicionais') return;

    const handleSecretVerify = (event: KeyboardEvent) => {
      if (event.ctrlKey && !event.shiftKey && !event.altKey && event.key === '1') {
        event.preventDefault();
        void verificarPagamentosEventoHandler();
      }
    };

    window.addEventListener('keydown', handleSecretVerify);
    return () => window.removeEventListener('keydown', handleSecretVerify);
  }, [activeTab, verificarPagamentosEventoHandler]);

  const totalArrecadadoEvento = comprasEvento.filter((compra) => compra.pago).reduce((sum, compra) => sum + compra.valorTotal, 0);
  const comprasPendentesEvento = comprasEvento.filter((compra) => !compra.pago).length;

  const availableYears = useMemo(() => {
    return getAvailableAdminPeriods(inscricoes);
  }, [inscricoes]);

  useEffect(() => {
    if (availableYears.length > 0 && !availableYears.includes(selectedYear)) {
      setSelectedYear(availableYears[0]);
    }
  }, [availableYears, selectedYear]);

  useEffect(() => {
    setEstoque(lerEstoqueSalvo(selectedYear));
  }, [selectedYear]);

  const yearInscricoes = useMemo(
    () =>
      inscricoes
        .filter((inscricao) => getInscricaoYear(inscricao) === selectedYear)
        .sort((a, b) => a.nomeCompleto.localeCompare(b.nomeCompleto, 'pt-BR')),
    [inscricoes, selectedYear]
  );


  const listaInscricoes = useMemo(
    () =>
      yearInscricoes
        .slice()
        .sort((a, b) => {
          const da = getInscricaoDate(a)?.getTime() ?? 0;
          const db = getInscricaoDate(b)?.getTime() ?? 0;
          return da - db;
        }),
    [yearInscricoes]
  );

  const formatarDataInscricao = (inscricao: Colonia) => {
    const d = getInscricaoDate(inscricao);
    if (!d) return '-';
    return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const relatorioTurmas = useMemo(() => {
    const dynamicTurmas = Array.from(new Set(yearInscricoes.map((item) => item.turma || 'Sem turma')));
    return Array.from(new Set([...turmasPadrao, ...dynamicTurmas]))
      .map((turma) => ({
        turma,
        criancas: yearInscricoes.filter((item) => (item.turma || 'Sem turma') === turma)
      }))
      .filter((item) => item.criancas.length > 0);
  }, [yearInscricoes]);

  const relatorioCamisas = useMemo(() => {
    const dynamicTamanhos = Array.from(new Set(yearInscricoes.map((item) => item.tamanhoCamisa || 'Não informado')));
    return Array.from(new Set([...tamanhosPadrao, ...dynamicTamanhos]))
      .map((tamanho) => ({
        tamanho,
        criancas: yearInscricoes.filter((item) => (item.tamanhoCamisa || 'Não informado') === tamanho)
      }))
      .filter((item) => item.criancas.length > 0);
  }, [yearInscricoes]);

  /** Tamanhos editáveis no estoque: os padrão, os que aparecem nas inscrições e os já salvos. */
  const tamanhosComEstoque = useMemo(() => {
    const dosDados = relatorioCamisas.map((item) => item.tamanho);
    return Array.from(new Set([...tamanhosPadrao, ...dosDados, ...Object.keys(estoque)])).filter(
      (tamanho) => tamanho !== 'Não informado'
    );
  }, [relatorioCamisas, estoque]);

  /** Linhas do comparativo: tudo que é necessário ou que foi encomendado. */
  const comparativoCamisas = useMemo(() => {
    const necessarioPorTamanho = new Map(relatorioCamisas.map((item) => [item.tamanho, item.criancas.length]));
    const tamanhos = Array.from(new Set([...tamanhosComEstoque, ...Array.from(necessarioPorTamanho.keys())]));
    return tamanhos
      .map((tamanho) => ({
        tamanho,
        necessario: necessarioPorTamanho.get(tamanho) || 0,
        estoque: estoque[tamanho] || 0
      }))
      .filter((item) => item.necessario > 0 || item.estoque > 0);
  }, [relatorioCamisas, tamanhosComEstoque, estoque]);

  const sabemNadar = yearInscricoes.filter((item) => item.sabeNadar);
  const naoSabemNadar = yearInscricoes.filter((item) => !item.sabeNadar);
  const somenteBoia = yearInscricoes.filter((item) => item.autorizacaoPiscina === 'somente_boia');
  const alergias = yearInscricoes.filter((item) => item.alergias && item.alergias.trim());
  const doencas = yearInscricoes.filter((item) => item.doencas && item.doencas.trim());
  const medicamentos = yearInscricoes.filter((item) => item.medicamentos && item.medicamentos.trim());
  const aniversariantes = yearInscricoes.filter((item) => {
    const monthDay = getMonthDay(item.dataNascimento);
    return monthDay >= '01-01' && monthDay <= '01-31';
  });
  const receita = yearInscricoes.reduce((sum, item) => sum + item.valorTotal, 0);
  const totalGastos = gastos.reduce((sum, item) => sum + totalDoGasto(item), 0);
  const saldo = receita - totalGastos;


  const anoBaseRelatorio = Number(String(selectedYear).slice(0, 4)) || new Date().getFullYear();
  const chamadasDoPeriodo = useMemo(
    () => chamadas.filter((chamada) => String(chamada.data || '').startsWith(String(anoBaseRelatorio))),
    [chamadas, anoBaseRelatorio]
  );

  const chamadaKey = (item: Colonia) => item.id || item.nomeCompleto;
  const chamadaPresente = (presenca?: { entrada?: boolean; saida?: boolean }) => Boolean(presenca?.entrada || presenca?.saida);

  const gerarRelatorioFinal = async () => {
    setGerandoRelatorioFinal(true);
    try {
      const comprasPagas = comprasEvento.filter((compra) => compra.pago);
      const comprasPendentes = comprasEvento.filter((compra) => !compra.pago);
      const totalAdicionaisRecebido = comprasPagas.reduce((sum, compra) => sum + Number(compra.valorTotal || 0), 0);
      const totalReceitas = receita + totalAdicionaisRecebido;
      const saldoFinal = totalReceitas - totalGastos;
      const totalDiasChamada = chamadasDoPeriodo.length;

      const presencaPorAluno = yearInscricoes.map((inscricao) => {
        const key = chamadaKey(inscricao);
        let diasPresente = 0;
        let entradas = 0;
        let saidas = 0;
        let piscina = 0;
        chamadasDoPeriodo.forEach((chamada) => {
          const presenca = chamada.presencas?.[key];
          if (chamadaPresente(presenca)) diasPresente += 1;
          if (presenca?.entrada) entradas += 1;
          if (presenca?.saida) saidas += 1;
          if (presenca?.piscina === 'sim') piscina += 1;
        });
        return { inscricao, diasPresente, entradas, saidas, piscina };
      });

      const chamadasResumo = chamadasDoPeriodo
        .slice()
        .sort((a, b) => String(a.data).localeCompare(String(b.data)))
        .map((chamada) => {
          let presentes = 0;
          let entradas = 0;
          let saidas = 0;
          let piscina = 0;
          yearInscricoes.forEach((inscricao) => {
            const presenca = chamada.presencas?.[chamadaKey(inscricao)];
            if (chamadaPresente(presenca)) presentes += 1;
            if (presenca?.entrada) entradas += 1;
            if (presenca?.saida) saidas += 1;
            if (presenca?.piscina === 'sim') piscina += 1;
          });
          return { data: chamada.data, presentes, entradas, saidas, piscina };
        });

      const gastosPorCategoria = categoriasGasto.map((categoria) => {
        const itens = gastos.filter((gasto) => gasto.categoria === categoria);
        return { categoria, itens: itens.length, total: itens.reduce((sum, gasto) => sum + totalDoGasto(gasto), 0) };
      }).filter((item) => item.itens > 0 || item.total > 0);

      const adicionaisPorItem = itensEvento.map((item) => {
        const comprasComItem = comprasEvento.filter((compra) => (compra.itens || []).some((entrada) => entrada.id === item.id));
        const quantidade = comprasComItem.reduce((sum, compra) => {
          const entrada = (compra.itens || []).find((itemCompra) => itemCompra.id === item.id);
          return sum + Number(entrada?.quantidade || 0);
        }, 0);
        const pagos = comprasComItem.filter((compra) => compra.pago).length;
        const pendentes = comprasComItem.length - pagos;
        const recebido = comprasComItem.filter((compra) => compra.pago).reduce((sum, compra) => {
          const entrada = (compra.itens || []).find((itemCompra) => itemCompra.id === item.id);
          return sum + Number(entrada?.valor || 0) * Number(entrada?.quantidade || 0);
        }, 0);
        return { item, quantidade, pagos, pendentes, recebido };
      });

      const dados: RelatorioFinalDados = {
        periodo: selectedYear,
        stats: [
          { label: 'Inscricoes', valor: String(yearInscricoes.length), cor: [31, 111, 159] },
          { label: 'Receita total', valor: formatCurrency(totalReceitas), cor: [22, 163, 74] },
          { label: 'Gastos', valor: formatCurrency(totalGastos), cor: [220, 38, 38] },
          { label: 'Saldo', valor: formatCurrency(saldoFinal), cor: saldoFinal >= 0 ? [22, 163, 74] : [220, 38, 38] }
        ],
        tabelas: [
          {
            titulo: '1. Resumo executivo',
            cabecalho: ['Indicador', 'Resultado', 'Observacao'],
            larguras: [48, 42, 84],
            linhas: [
              { colunas: ['Periodo analisado', selectedYear, `Ano-base das chamadas: ${anoBaseRelatorio}`] },
              { colunas: ['Inscricoes', String(yearInscricoes.length), `${yearInscricoes.filter((item) => item.pago).length} pagas / ${yearInscricoes.filter((item) => item.gratuito).length} gratuitas`] },
              { colunas: ['Presenca', `${totalDiasChamada} dias`, 'Conta presenca quando entrada ou saida foi marcada.'] },
              { colunas: ['Termos', `${inscricoesComTermo.length} assinados`, `${inscricoesSemTermo.length} pendentes`] },
              { colunas: ['Adicionais', `${comprasEvento.length} pedidos`, `${comprasPagas.length} pagos / ${comprasPendentes.length} pendentes`] }
            ]
          },
          {
            titulo: '2. Financeiro consolidado',
            cabecalho: ['Descricao', 'Valor', 'Criterio'],
            larguras: [74, 38, 62],
            linhas: [
              { colunas: ['Receita de inscricoes', formatCurrency(receita), 'Soma do valor total das inscricoes do periodo.'] },
              { colunas: ['Receita de adicionais recebidos', formatCurrency(totalAdicionaisRecebido), 'Somente pedidos de adicionais confirmados/pagos.'] },
              { colunas: ['Receita total considerada', formatCurrency(totalReceitas), 'Inscricoes + adicionais pagos.'] },
              { colunas: ['Gastos registrados', formatCurrency(totalGastos), `${gastos.length} lancamento(s).`] },
              { colunas: ['Saldo final', formatCurrency(saldoFinal), 'Receita total - gastos registrados.'] }
            ]
          },
          {
            titulo: '3. Inscricoes por turma',
            cabecalho: ['Turma', 'Alunos', 'Pagos', 'Gratuitos', 'Receita'],
            larguras: [46, 28, 28, 30, 42],
            linhas: relatorioTurmas.map(({ turma, criancas }) => ({
              colunas: [turma, String(criancas.length), String(criancas.filter((item) => item.pago).length), String(criancas.filter((item) => item.gratuito).length), formatCurrency(criancas.reduce((sum, item) => sum + Number(item.valorTotal || 0), 0))]
            })),
            rodape: `${yearInscricoes.length} inscricao(oes) no periodo.`
          },
          {
            titulo: '4. Camisas',
            cabecalho: ['Tamanho', 'Necessario', 'Estoque', 'Diferenca'],
            larguras: [45, 42, 42, 45],
            linhas: comparativoCamisas.map((item) => ({ colunas: [item.tamanho, String(item.necessario), String(item.estoque), String(item.estoque - item.necessario)] }))
          },
          {
            titulo: '5. Presencas por dia',
            cabecalho: ['Data', 'Presentes', 'Entradas', 'Saidas', 'Piscina'],
            larguras: [42, 35, 32, 32, 33],
            linhas: chamadasResumo.map((item) => ({ colunas: [formatarDataCurta(item.data), String(item.presentes), String(item.entradas), String(item.saidas), String(item.piscina)] })),
            rodape: 'Presente = entrada marcada ou saida marcada. Saida isolada tambem conta como presenca.'
          },
          {
            titulo: '6. Presenca por crianca',
            cabecalho: ['Crianca', 'Turma', 'Dias', 'Entradas', 'Saidas'],
            larguras: [72, 38, 22, 24, 24],
            linhas: presencaPorAluno.map(({ inscricao, diasPresente, entradas, saidas }) => ({ colunas: [inscricao.nomeCompleto, inscricao.turma || 'Sem turma', String(diasPresente), String(entradas), String(saidas)] }))
          },
          {
            titulo: '7. Gastos por categoria',
            cabecalho: ['Categoria', 'Lancamentos', 'Total'],
            larguras: [82, 42, 50],
            linhas: gastosPorCategoria.map((item) => ({ colunas: [item.categoria, String(item.itens), formatCurrency(item.total)] }))
          },
          {
            titulo: '8. Gastos detalhados',
            cabecalho: ['Data', 'Categoria', 'Descricao', 'Total'],
            larguras: [30, 38, 76, 30],
            linhas: gastos.map((gasto) => ({ colunas: [formatarDataCurta(gasto.data), gasto.categoria, gasto.descricao, formatCurrency(totalDoGasto(gasto))] }))
          },
          {
            titulo: '9. Adicionais do evento',
            cabecalho: ['Item', 'Qtd.', 'Pagos', 'Pendentes', 'Recebido'],
            larguras: [58, 26, 28, 32, 36],
            linhas: adicionaisPorItem.map(({ item, quantidade, pagos, pendentes, recebido }) => ({ colunas: [item.nome, String(quantidade), String(pagos), String(pendentes), formatCurrency(recebido)] }))
          },
          {
            titulo: '10. Pendencias administrativas',
            cabecalho: ['Tipo', 'Quantidade', 'Detalhe'],
            larguras: [54, 34, 86],
            linhas: [
              { colunas: ['Inscricoes pendentes', String(yearInscricoes.filter((item) => item.status === 'pendente' && !item.pago && !item.gratuito).length), 'Inscricoes sem pagamento confirmado.'] },
              { colunas: ['Termos nao assinados', String(inscricoesSemTermo.length), 'Responsaveis que ainda nao aparecem na base de termos.'] },
              { colunas: ['Adicionais pendentes', String(comprasPendentes.length), 'Pedidos de adicionais ainda sem pagamento confirmado.'] },
              { colunas: ['Saida sem entrada', String(presencaPorAluno.filter((item) => item.saidas > item.entradas).length), 'Casos onde a saida marcada indica presenca, mesmo sem entrada.'] }
            ]
          }
        ]
      };

      baixarRelatorioFinalPdf(dados);
    } finally {
      setGerandoRelatorioFinal(false);
    }
  };

  const idsComTermoAssinado = useMemo(
    () => new Set(termosAutorizacao.map((termo) => termo.inscricaoId)),
    [termosAutorizacao]
  );
  const inscricoesComTermo = yearInscricoes.filter((item) => item.id && idsComTermoAssinado.has(item.id));
  const inscricoesSemTermo = yearInscricoes.filter((item) => !item.id || !idsComTermoAssinado.has(item.id));
  const termoPorInscricaoId = useMemo(
    () => new Map(termosAutorizacao.map((termo) => [termo.inscricaoId, termo])),
    [termosAutorizacao]
  );


  const printCurrentReport = () => window.print();

  const gerarRelatorioTurma = (turma: string, criancas: Colonia[]) => {
    void generateReportImage({
      ano: selectedYear,
      titulo: `Turma ${turma}`,
      subtitulo: `Colônia de Férias • Período ${selectedYear}`,
      stats: [
        { label: 'ALUNOS NA TURMA', valor: String(criancas.length), tom: 'primary' },
        { label: 'SABEM NADAR', valor: String(criancas.filter((item) => item.sabeNadar).length), tom: 'green' },
        {
          label: 'ATENÇÃO NA SAÚDE',
          valor: String(
            criancas.filter((item) => item.alergias?.trim() || item.doencas?.trim() || item.medicamentos?.trim()).length
          ),
          tom: 'red'
        }
      ],
      secoes: [
        {
          titulo: 'Alunos',
          itens: criancas.map((item) => paraItemRelatorio(item, (crianca) => crianca.nomeMae))
        }
      ],
      rodape: `${criancas.length} ${criancas.length === 1 ? 'aluno' : 'alunos'} • ${turma}`,
      arquivo: `turma-${turma.replace(/[^\w]+/g, '-').toLowerCase()}-${selectedYear}.png`
    });
  };

  const gerarRelatorioTurmas = () => {
    void generateReportImage({
      ano: selectedYear,
      titulo: 'Relatório por Turma',
      stats: [
        { label: 'TOTAL DE ALUNOS', valor: String(yearInscricoes.length), tom: 'primary' },
        { label: 'TURMAS', valor: String(relatorioTurmas.length), tom: 'title' }
      ],
      secoes: relatorioTurmas.map((item) => ({
        titulo: item.turma,
        itens: item.criancas.map((crianca) => paraItemRelatorio(crianca, (c) => c.nomeMae))
      })),
      rodape: `${yearInscricoes.length} alunos • ${relatorioTurmas.length} turmas`,
      arquivo: `relatorio-turmas-${selectedYear}.png`
    });
  };

  const gerarRelatorioNatacao = () => {
    void generateReportImage({
      ano: selectedYear,
      titulo: 'Natação e Piscina',
      stats: [
        { label: 'SABEM NADAR', valor: String(sabemNadar.length), tom: 'green' },
        { label: 'NÃO SABEM NADAR', valor: String(naoSabemNadar.length), tom: 'red' },
        { label: 'SOMENTE COM BOIA', valor: String(somenteBoia.length), tom: 'primary' }
      ],
      secoes: [
        {
          titulo: 'Não sabem nadar — atenção redobrada',
          itens: naoSabemNadar.map((item) => paraItemRelatorio(item)),
          vazio: 'Todos os alunos sabem nadar.'
        },
        {
          titulo: 'Somente com boia',
          itens: somenteBoia.map((item) => paraItemRelatorio(item)),
          vazio: 'Nenhum aluno restrito à boia.'
        }
      ],
      rodape: `${naoSabemNadar.length} não sabem nadar • ${somenteBoia.length} só com boia`,
      arquivo: `natacao-${selectedYear}.png`
    });
  };

  const gerarRelatorioSaude = () => {
    void generateReportImage({
      ano: selectedYear,
      titulo: 'Relatório de Saúde',
      stats: [
        { label: 'ALERGIAS', valor: String(alergias.length), tom: 'red' },
        { label: 'DOENÇAS', valor: String(doencas.length), tom: 'red' },
        { label: 'MEDICAMENTOS', valor: String(medicamentos.length), tom: 'primary' }
      ],
      secoes: [
        {
          titulo: 'Alergias',
          itens: alergias.map((item) => paraItemRelatorio(item, (crianca) => crianca.alergias)),
          vazio: 'Nenhuma alergia registrada.'
        },
        {
          titulo: 'Doenças',
          itens: doencas.map((item) => paraItemRelatorio(item, (crianca) => crianca.doencas)),
          vazio: 'Nenhuma doença registrada.'
        },
        {
          titulo: 'Medicamentos',
          itens: medicamentos.map((item) => paraItemRelatorio(item, (crianca) => crianca.medicamentos)),
          vazio: 'Nenhum medicamento registrado.'
        }
      ],
      rodape: `${alergias.length} alergias • ${doencas.length} doenças • ${medicamentos.length} medicamentos`,
      arquivo: `saude-${selectedYear}.png`
    });
  };

  const gerarImagemResumoAdicionais = () => {
    const comprasPagas = comprasEvento.filter((compra) => compra.pago);
    const comprasPendentes = comprasEvento.filter((compra) => !compra.pago);
    const resumoItens = itensEvento.map((item) => {
      const comprasComItem = comprasEvento.filter((compra) => (compra.itens || []).some((entrada) => entrada.id === item.id));
      const quantidade = comprasComItem.reduce((sum, compra) => {
        const entrada = (compra.itens || []).find((itemCompra) => itemCompra.id === item.id);
        return sum + Number(entrada?.quantidade || 0);
      }, 0);
      const pagos = comprasComItem.filter((compra) => compra.pago).length;
      const pendentes = comprasComItem.length - pagos;
      const arrecadado = comprasComItem
        .filter((compra) => compra.pago)
        .reduce((sum, compra) => {
          const entrada = (compra.itens || []).find((itemCompra) => itemCompra.id === item.id);
          return sum + Number(entrada?.valor || 0) * Number(entrada?.quantidade || 0);
        }, 0);
      return { item, quantidade, pagos, pendentes, arrecadado };
    });

    void generateReportImage({
      ano: selectedYear,
      titulo: 'Resumo dos Adicionais do Evento',
      subtitulo: `Bingo e Cachorro-quente � ${new Date().toLocaleDateString('pt-BR')}`,
      stats: [
        { label: 'ARRECADADO', valor: formatCurrency(totalArrecadadoEvento), tom: 'green' },
        { label: 'PENDENTES', valor: String(comprasPendentes.length), tom: comprasPendentes.length ? 'red' : 'green' },
        { label: 'INSCRI��ES', valor: String(comprasEvento.length), tom: 'primary' },
        { label: 'ITENS VENDIDOS', valor: String(resumoItens.reduce((sum, item) => sum + item.quantidade, 0)), tom: 'title' }
      ],
      secoes: [
        {
          titulo: 'Resumo por item',
          itens: resumoItens.map(({ item, quantidade, pagos, pendentes, arrecadado }) => ({
            nome: `${item.nome}: ${quantidade}`,
            detalhe: `${formatCurrency(arrecadado)} recebidos � ${pagos} pagos � ${pendentes} pendentes`
          })),
          vazio: 'Nenhum item configurado.'
        },
        {
          titulo: 'Pagamentos confirmados',
          itens: comprasPagas.map((compra) => ({
            nome: compra.nomeCrianca || 'Crian�a n�o identificada',
            detalhe: `${compra.nomeResponsavel} � ${(compra.itens || []).map((item) => item.quantidade > 1 ? `${item.nome} x${item.quantidade}` : item.nome).join(', ')} � ${formatCurrency(compra.valorTotal)}`
          })),
          vazio: 'Nenhum pagamento confirmado.'
        },
        {
          titulo: 'Pagamentos pendentes',
          itens: comprasPendentes.map((compra) => ({
            nome: compra.nomeCrianca || 'Crian�a n�o identificada',
            detalhe: `${compra.nomeResponsavel} � ${(compra.itens || []).map((item) => item.quantidade > 1 ? `${item.nome} x${item.quantidade}` : item.nome).join(', ')} � ${formatCurrency(compra.valorTotal)}`
          })),
          vazio: 'Nenhuma pend�ncia nos adicionais.'
        }
      ],
      rodape: `${comprasPagas.length} pagos � ${comprasPendentes.length} pendentes � ${formatCurrency(totalArrecadadoEvento)} recebidos`,
      arquivo: `resumo-adicionais-evento-${selectedYear}.png`
    });
  };

  const gerarRelatorioAniversariantes = () => {
    void generateReportImage({
      ano: selectedYear,
      titulo: 'Aniversariantes da Colônia',
      subtitulo: `Janeiro • Período ${selectedYear}`,
      stats: [{ label: 'ANIVERSARIANTES EM JANEIRO', valor: String(aniversariantes.length), tom: 'primary' }],
      secoes: [
        {
          titulo: 'Aniversariantes',
          itens: aniversariantes.map((item) =>
            paraItemRelatorio(item, (crianca) => `${formatarDataCurta(crianca.dataNascimento)} • ${crianca.turma || 'Sem turma'}`)
          ),
          vazio: 'Nenhum aniversariante em janeiro.'
        }
      ],
      rodape: `${aniversariantes.length} ${aniversariantes.length === 1 ? 'aniversariante' : 'aniversariantes'}`,
      arquivo: `aniversariantes-${selectedYear}.png`
    });
  };


  const exportarPdfGastos = () => {
    baixarPdfGastos(gastos, selectedYear, receita);
  };

  const gerarResumoCamisas = () => {
    void generateShirtSummaryImage({
      ano: selectedYear,
      itens: relatorioCamisas.map((item) => ({ tamanho: item.tamanho, quantidade: item.criancas.length }))
    });
  };

  const gerarComparativoCamisas = () => {
    void generateShirtStockImage({ ano: selectedYear, itens: comparativoCamisas });
  };

  const salvarGasto = async (event: React.FormEvent) => {
    event.preventDefault();
    setGastoErro('');

    const quantidade = Number(novoGasto.quantidade.replace(',', '.'));
    const valorUnitario = Number(novoGasto.valorUnitario.replace(',', '.'));
    if (!novoGasto.descricao.trim()) return setGastoErro('Informe a descrição do gasto.');
    if (!Number.isFinite(quantidade) || quantidade <= 0) return setGastoErro('Informe uma quantidade maior que zero.');
    if (!Number.isFinite(valorUnitario) || valorUnitario < 0) return setGastoErro('Informe um valor unitário válido.');

    setGastoSalvando(true);
    try {
      const criado = await createGasto({
        periodo: selectedYear,
        descricao: novoGasto.descricao.trim(),
        categoria: novoGasto.categoria,
        quantidade,
        valorUnitario,
        data: novoGasto.data
      });
      setGastos((atuais) => [criado, ...atuais]);
      setNovoGasto({ descricao: '', categoria: categoriasGasto[0], quantidade: '1', valorUnitario: '', data: '' });
    } catch (saveError) {
      setGastoErro((saveError as Error).message);
    } finally {
      setGastoSalvando(false);
    }
  };

  const excluirGasto = async (gasto: Gasto) => {
    if (!window.confirm(`Excluir o gasto "${gasto.descricao}"?`)) return;
    setGastoErro('');
    try {
      await deleteGasto(gasto.id);
      setGastos((atuais) => atuais.filter((item) => item.id !== gasto.id));
    } catch (deleteError) {
      setGastoErro((deleteError as Error).message);
    }
  };

  const atualizarEstoque = (tamanho: string, valor: string) => {
    const numero = Math.max(0, Math.floor(Number(valor)) || 0);
    setEstoque((anterior) => {
      const proximo = { ...anterior, [tamanho]: numero };
      try {
        window.localStorage.setItem(estoqueStorageKey(selectedYear), JSON.stringify(proximo));
      } catch {
        // Sem localStorage o valor vale só para esta sessão.
      }
      return proximo;
    });
  };

  const renderNameList = (items: Colonia[]) => (
    <div className="report-name-list">
      {items.map((item) => (
        <div className="report-name-row" key={item.id || item.nomeCompleto}>
          {item.fotoUrl ? <img src={item.fotoUrl} alt="" /> : <span>{item.nomeCompleto.slice(0, 2)}</span>}
          <div>
            <strong>{item.nomeCompleto}</strong>
            <small>{item.turma || 'Sem turma'}</small>
          </div>
        </div>
      ))}
    </div>
  );

  const renderTermosAssinadosList = (items: Colonia[]) => (
    <div className="report-name-list">
      {items.map((item) => (
        <button
          type="button"
          className="report-name-row clicavel"
          key={item.id || item.nomeCompleto}
          onClick={() => item.id && setTermoSelecionado(termoPorInscricaoId.get(item.id) || null)}
        >
          {item.fotoUrl ? <img src={item.fotoUrl} alt="" /> : <span>{item.nomeCompleto.slice(0, 2)}</span>}
          <div>
            <strong>{item.nomeCompleto}</strong>
            <small>{item.turma || 'Sem turma'}</small>
          </div>
        </button>
      ))}
    </div>
  );

  const inscricoesPagas = yearInscricoes.filter((item) => item.pago).length;
  const inscricoesPendentes = yearInscricoes.filter((item) => item.status === 'pendente' && !item.pago && !item.gratuito).length;

  const reportTabs: ReportNavigationItem[] = [
    { id: 'turmas', label: 'Turmas', description: 'Distribuição por turma e faixa etária.', metric: `${relatorioTurmas.length} turmas`, group: 'Operação', icon: UsersRound },
    { id: 'camisas', label: 'Camisas', description: 'Tamanhos, estoque e comparativo.', metric: `${relatorioCamisas.length} tamanhos`, group: 'Operação', icon: Shirt },
    { id: 'natacao', label: 'Natação', description: 'Piscina, boia e autonomia na água.', metric: `${naoSabemNadar.length} atenções`, group: 'Operação', icon: Waves },
    { id: 'saude', label: 'Saúde', description: 'Alergias, doenças e medicamentos.', metric: `${alergias.length + doencas.length + medicamentos.length} registros`, group: 'Operação', icon: HeartPulse },
    { id: 'aniversariantes', label: 'Aniversariantes', description: 'Crianças aniversariantes do período.', metric: `${aniversariantes.length} nomes`, group: 'Operação', icon: CalendarDays },
    { id: 'adicionais', label: 'Adicionais', description: 'Bingo, cachorro-quente e pagamentos.', metric: `${comprasEvento.length} pedidos`, group: 'Financeiro', icon: Grid3x3 },
    { id: 'gastos', label: 'Gastos', description: 'Lançamentos, saldo e PDF financeiro.', metric: `${gastos.length} itens`, group: 'Financeiro', icon: WalletCards },
    { id: 'lista', label: 'Inscrições', description: 'Lista impressa e conferência geral.', metric: `${yearInscricoes.length} nomes`, group: 'Documentos', icon: ListChecks },
    { id: 'carteirinhas', label: 'Carteirinhas', description: 'Geração das carteirinhas dos alunos.', metric: 'PDF', group: 'Documentos', icon: ClipboardCheck },
    { id: 'termo', label: 'Termos', description: 'Autorizações assinadas e pendentes.', metric: `${inscricoesComTermo.length} assinados`, group: 'Documentos', icon: FileText },
    { id: 'final', label: 'Relatório final', description: 'Documento completo para administração.', metric: 'Completo', group: 'Documentos', icon: BarChart3 }
  ];

  const overviewMetrics: ReportMetricItem[] = [
    { label: 'Inscrições', value: yearInscricoes.length, icon: UsersRound },
    { label: 'Pagas', value: inscricoesPagas, tone: 'positive', icon: CheckCheck },
    { label: 'Pendentes', value: inscricoesPendentes, tone: inscricoesPendentes ? 'warning' : 'positive', icon: Receipt },
    { label: 'Receita', value: formatCurrency(receita), icon: WalletCards },
    { label: 'Adicionais', value: formatCurrency(totalArrecadadoEvento), tone: 'positive', icon: Grid3x3 }
  ];

  return (
    <div className="legacy-admin">
      <AdminTopbar
        inscricoes={inscricoes}
        selectedYear={selectedYear}
        title="Painel Administrativo"
        onYearChange={setSelectedYear}
      />

      <main className="legacy-content reports-page">
        <div className="reports-shell">
          <ReportsOverview
            title="Relatórios"
            description="Acompanhe operação, financeiro e documentos sem misturar contextos."
            period={selectedYear}
            metrics={overviewMetrics}
            onBack={() => navigate('/gerenciamento-colonia')}
          />

          <div className="reports-layout">
            <ReportsNavigation
              items={reportTabs}
              activeId={activeTab}
              onChange={(id) => setActiveTab(id as ReportTab)}
            />

            <div className="reports-content">
        {loading && <div className="legacy-message">Carregando relatórios...</div>}
        {error && <div className="legacy-message error">{error}</div>}

        {!loading && !error && (
          <ReportPanel>
            {activeTab === 'turmas' && (
              <>
                <ReportHeader
                  title="Turmas e faixas etárias"
                  description="Veja a distribuição das crianças e exporte os grupos para conferência."
                  eyebrow="Operação"
                  actions={(
                    <button
                      className="legacy-action-button green"
                      type="button"
                      onClick={gerarRelatorioTurmas}
                      disabled={relatorioTurmas.length === 0}
                    >
                      <ImageDown size={15} />
                      Exportar todas
                    </button>
                  )}
                />
                <div className="report-grid">
                  {relatorioTurmas.map((item) => (
                    <article className="report-card" key={item.turma}>
                      <div className="report-card-head">
                        <div>
                          <h3>{item.turma}</h3>
                          <strong>{item.criancas.length}</strong>
                        </div>
                        <button
                          className="legacy-action-button dark-green"
                          type="button"
                          onClick={() => gerarRelatorioTurma(item.turma, item.criancas)}
                        >
                          <ImageDown size={15} />
                          Exportar
                        </button>
                      </div>
                      {renderNameList(item.criancas)}
                    </article>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'camisas' && (
              <>
                <ReportHeader
                  title="Camisas por tamanho"
                  description="Compare a demanda registrada com o estoque encomendado para identificar faltas e sobras."
                  eyebrow="Operação"
                  actions={(
                    <>
                    <button
                      className="legacy-action-button green"
                      type="button"
                      onClick={gerarResumoCamisas}
                      disabled={relatorioCamisas.length === 0}
                    >
                      <ImageDown size={15} />
                      Gerar Resumo (Imagem)
                    </button>
                    <button
                      className="legacy-action-button dark-green"
                      type="button"
                      onClick={gerarComparativoCamisas}
                      disabled={comparativoCamisas.length === 0}
                    >
                      <ClipboardCheck size={15} />
                      Comparar estoque
                    </button>
                    </>
                  )}
                />

                <ReportSection
                  title="Estoque encomendado"
                  description="Os valores ficam salvos neste navegador para o período selecionado."
                >
                  <div className="stock-editor-grid">
                    {tamanhosComEstoque.map((tamanho) => (
                      <label className="stock-editor-field" key={tamanho}>
                        <span>{tamanho}</span>
                        <input
                          type="number"
                          min={0}
                          value={estoque[tamanho] ?? 0}
                          onChange={(event) => atualizarEstoque(tamanho, event.target.value)}
                        />
                      </label>
                    ))}
                  </div>
                </ReportSection>

                <div className="shirt-grid">
                  {relatorioCamisas.map((item) => (
                    <article className="shirt-card" key={item.tamanho}>
                      <h3>Tamanho {item.tamanho}</h3>
                      <strong>Total: {item.criancas.length} unidades</strong>
                      {renderNameList(item.criancas)}
                    </article>
                  ))}
                </div>
              </>
            )}

            {activeTab === 'natacao' && (
              <>
                <ReportHeader
                  title="Natação e piscina"
                  description="Identifique rapidamente quem precisa de atenção adicional nas atividades aquáticas."
                  eyebrow="Segurança"
                  actions={(
                    <button className="legacy-action-button green" type="button" onClick={gerarRelatorioNatacao}>
                      <ImageDown size={15} />
                      Exportar imagem
                    </button>
                  )}
                />
                <ReportMetrics items={[
                  { label: 'Sabem nadar', value: sabemNadar.length, tone: 'positive', icon: Waves },
                  { label: 'Não sabem nadar', value: naoSabemNadar.length, tone: 'negative', icon: HeartPulse },
                  { label: 'Somente com boia', value: somenteBoia.length, tone: 'warning', icon: Waves }
                ]} />
                <div className="report-grid">
                  <article className="report-card">
                    <h3>Não sabem nadar</h3>
                    <strong>{naoSabemNadar.length}</strong>
                    {renderNameList(naoSabemNadar)}
                  </article>
                  <article className="report-card">
                    <h3>Somente com boia</h3>
                    <strong>{somenteBoia.length}</strong>
                    {renderNameList(somenteBoia)}
                  </article>
                </div>
              </>
            )}

            {activeTab === 'saude' && (
              <>
                <ReportHeader
                  title="Saúde e cuidados"
                  description="Centralize alergias, doenças e medicamentos informados pelos responsáveis."
                  eyebrow="Segurança"
                  actions={(
                    <button className="legacy-action-button green" type="button" onClick={gerarRelatorioSaude}>
                      <ImageDown size={15} />
                      Exportar imagem
                    </button>
                  )}
                />
                <ReportMetrics items={[
                  { label: 'Alergias', value: alergias.length, tone: alergias.length ? 'warning' : 'positive', icon: HeartPulse },
                  { label: 'Doenças', value: doencas.length, tone: doencas.length ? 'negative' : 'positive', icon: HeartPulse },
                  { label: 'Medicamentos', value: medicamentos.length, tone: medicamentos.length ? 'info' : 'positive', icon: ClipboardCheck }
                ]} />
                <div className="report-grid three">
                  <article className="report-card"><h3>Alergias</h3><strong>{alergias.length}</strong>{renderNameList(alergias)}</article>
                  <article className="report-card"><h3>Doenças</h3><strong>{doencas.length}</strong>{renderNameList(doencas)}</article>
                  <article className="report-card"><h3>Medicamentos</h3><strong>{medicamentos.length}</strong>{renderNameList(medicamentos)}</article>
                </div>
              </>
            )}

            {activeTab === 'adicionais' && (
              <>
                <ReportHeader
                  title="Adicionais do evento"
                  description="Configure os itens vendidos, acompanhe pagamentos e acesse as cartelas do bingo."
                  eyebrow="Financeiro"
                  actions={(
                    <>
                      <button className="legacy-action-button" type="button" onClick={copiarLinkEvento}>
                        <Copy size={15} />
                        {linkEventoCopiado ? 'Link copiado' : 'Copiar link'}
                      </button>
                      <button className="legacy-action-button" type="button" onClick={gerarImagemResumoAdicionais}>
                        <ImageDown size={15} />
                        Gerar resumo
                      </button>
                    <button className="legacy-action-button green" type="button" onClick={() => setMostrarCartelasBingo(true)}>
                      <Grid3x3 size={15} />
                      Cartelas
                    </button>
                    </>
                  )}
                />

                <ReportMetrics items={[
                  { label: 'Arrecadado', value: formatCurrency(totalArrecadadoEvento), tone: 'positive', icon: WalletCards },
                  { label: 'Pendentes', value: comprasPendentesEvento, tone: comprasPendentesEvento ? 'warning' : 'positive', icon: Receipt },
                  { label: 'Pedidos', value: comprasEvento.length, icon: ListChecks }
                ]} />

                <ReportSection
                  title="Itens e preços"
                  description="Essas opções aparecem no link público enviado aos responsáveis."
                >
                  <form
                    className="expense-form"
                    onSubmit={(event) => {
                      event.preventDefault();
                      void salvarItensEventoHandler();
                    }}
                  >
                    {itensEventoCarregando && <p className="report-muted">Carregando itens...</p>}
                    {!itensEventoCarregando && (
                      <div className="expense-list">
                        {itensEvento.map((item) => (
                          <div className="expense-row" key={item.id}>
                            <div className="expense-form-grid evento-item-grid">
                              <label className="expense-field wide">
                                <span>Nome do item</span>
                                <input
                                  value={item.nome}
                                  placeholder="Ex.: Bingo"
                                  onChange={(event) => editarItemEvento(item.id, { nome: event.target.value })}
                                />
                              </label>
                              <label className="expense-field">
                                <span>Valor (R$)</span>
                                <input
                                  inputMode="decimal"
                                  value={item.valor || ''}
                                  placeholder="0,00"
                                  onChange={(event) =>
                                    editarItemEvento(item.id, { valor: Number(event.target.value.replace(',', '.')) || 0 })
                                  }
                                />
                              </label>
                              <label className="expense-field evento-quantity-toggle">
                                <input
                                  type="checkbox"
                                  checked={Boolean(item.permiteQuantidade)}
                                  onChange={(event) => editarItemEvento(item.id, { permiteQuantidade: event.target.checked })}
                                />
                                <span>Permite comprar mais de um</span>
                              </label>
                            </div>
                            <button
                              type="button"
                              className="expense-delete"
                              title="Remover item"
                              onClick={() => removerItemEvento(item.id)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {itensEventoErro && <div className="expense-error">{itensEventoErro}</div>}
                    <div className="report-panel-actions" style={{ marginTop: 12 }}>
                      <button type="button" className="legacy-action-button" onClick={adicionarItemEvento}>
                        <Plus size={15} />
                        Adicionar item
                      </button>
                      <button type="submit" className="legacy-action-button green" disabled={itensEventoSalvando || !itensEvento.length}>
                        <CheckCheck size={15} />
                        {itensEventoSalvando ? 'Salvando...' : itensEventoSalvo ? 'Salvo!' : 'Salvar itens'}
                      </button>
                    </div>
                  </form>
                </ReportSection>

                <ReportSection
                  title="Pedidos e pagamentos"
                  description="Acompanhe o que cada responsável pediu e o status da cobrança."
                >
                  {comprasEventoCarregando && <p className="report-muted">Carregando inscrições do evento...</p>}
                  {comprasEventoErro && <div className="expense-error">{comprasEventoErro}</div>}
                  {!comprasEventoCarregando && !comprasEvento.length && (
                    <ReportEmptyState
                      icon={Receipt}
                      title="Nenhum pedido por enquanto"
                      description="Os pedidos feitos no link público aparecerão aqui automaticamente."
                    />
                  )}
                  {!comprasEventoCarregando && comprasEvento.length > 0 && (
                    <div className="evento-compra-lista">
                      {comprasEvento.map((compra) => (
                        <div className="evento-compra-card" key={compra.id}>
                          <div className="evento-compra-topo">
                            <div className="evento-compra-info">
                              <strong>
                                {compra.nomeCrianca || 'Criança não identificada'}
                                {compra.turma ? ` (${compra.turma})` : ''}
                              </strong>
                              <small>
                                Responsável: {compra.nomeResponsavel} •{' '}
                                {(compra.itens || [])
                                  .map((item) => (item.quantidade > 1 ? `${item.nome} x${item.quantidade}` : item.nome))
                                  .join(', ')}
                              </small>
                              <span className={`evento-status-badge ${compra.pago ? 'pago' : 'pendente'}`}>
                                {compra.pago ? 'Pago' : 'Pendente'}
                              </span>
                            </div>
                            <div className="evento-compra-valor">
                              <strong className={compra.pago ? 'positive' : 'negative'}>{formatCurrency(compra.valorTotal)}</strong>
                            </div>
                          </div>
                          <div className="evento-compra-acoes">
                            {compra.invoiceUrl && (
                              <button
                                type="button"
                                className="evento-compra-acao"
                                onClick={() => window.open(compra.invoiceUrl, '_blank')}
                              >
                                <Receipt size={13} />
                                Ver fatura
                              </button>
                            )}
                            {!compra.pago && compra.telefoneResponsavel && (
                              <button
                                type="button"
                                className="evento-compra-acao whatsapp"
                                onClick={() => avisarPendenciaEventoWhatsapp(compra)}
                              >
                                <MessageCircle size={13} />
                                Avisar pendência
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ReportSection>

                {mostrarCartelasBingo && (
                  <div className="admin-floating-backdrop" role="presentation" onClick={() => setMostrarCartelasBingo(false)}>
                    <div
                      className="admin-floating-panel wide"
                      role="dialog"
                      aria-modal="true"
                      aria-label="Cartelas de Bingo"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <div className="admin-floating-header">
                        <h3>
                          <Grid3x3 size={16} />
                          Cartelas Bingo
                        </h3>
                        <button type="button" onClick={() => setMostrarCartelasBingo(false)} aria-label="Fechar">
                          <X size={18} />
                        </button>
                      </div>
                      <CartelasBingoPanel />
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'aniversariantes' && (
              <>
                <ReportHeader
                  title="Aniversariantes"
                  description="Crianças que fazem aniversário durante o período considerado."
                  eyebrow="Operação"
                  actions={(
                    <button className="legacy-action-button green" type="button" onClick={gerarRelatorioAniversariantes}>
                      <ImageDown size={15} />
                      Exportar imagem
                    </button>
                  )}
                />
                {aniversariantes.length > 0 ? renderNameList(aniversariantes) : (
                  <ReportEmptyState
                    icon={CalendarDays}
                    title="Nenhum aniversariante no período"
                    description="Quando houver uma criança aniversariante, ela aparecerá nesta lista."
                  />
                )}
              </>
            )}

            {activeTab === 'gastos' && (
              <>
                <ReportHeader
                  title="Produtos e gastos"
                  description="Registre despesas, acompanhe o saldo do período e gere o documento financeiro."
                  eyebrow="Financeiro"
                  actions={(
                    <button className="legacy-action-button green" type="button" onClick={exportarPdfGastos}>
                      <FileDown size={15} />
                      Exportar PDF
                    </button>
                  )}
                />

                <ReportMetrics items={[
                  { label: 'Receita', value: formatCurrency(receita), tone: 'positive', icon: WalletCards },
                  { label: 'Gastos', value: formatCurrency(totalGastos), tone: 'negative', icon: Receipt },
                  { label: 'Saldo', value: formatCurrency(saldo), tone: saldo >= 0 ? 'positive' : 'negative', icon: BarChart3 },
                  { label: 'Inscrições', value: yearInscricoes.length, icon: UsersRound }
                ]} />

                <ReportSection
                  title="Novo lançamento"
                  description="O lançamento fica disponível para todos os administradores."
                >
                  <form className="expense-form" onSubmit={salvarGasto}>
                  <div className="expense-form-grid">
                    <label className="expense-field wide">
                      <span>Descrição</span>
                      <input
                        value={novoGasto.descricao}
                        placeholder="Ex.: Camisas tamanho 10"
                        onChange={(event) => setNovoGasto((atual) => ({ ...atual, descricao: event.target.value }))}
                      />
                    </label>
                    <label className="expense-field">
                      <span>Categoria</span>
                      <select
                        value={novoGasto.categoria}
                        onChange={(event) => setNovoGasto((atual) => ({ ...atual, categoria: event.target.value }))}
                      >
                        {categoriasGasto.map((categoria) => (
                          <option key={categoria} value={categoria}>{categoria}</option>
                        ))}
                      </select>
                    </label>
                    <label className="expense-field">
                      <span>Quantidade</span>
                      <input
                        type="number"
                        min={1}
                        step="1"
                        value={novoGasto.quantidade}
                        onChange={(event) => setNovoGasto((atual) => ({ ...atual, quantidade: event.target.value }))}
                      />
                    </label>
                    <label className="expense-field">
                      <span>Valor unitário (R$)</span>
                      <input
                        type="number"
                        min={0}
                        step="0.01"
                        value={novoGasto.valorUnitario}
                        placeholder="0,00"
                        onChange={(event) => setNovoGasto((atual) => ({ ...atual, valorUnitario: event.target.value }))}
                      />
                    </label>
                    <label className="expense-field">
                      <span>Data</span>
                      <input
                        type="date"
                        value={novoGasto.data}
                        onChange={(event) => setNovoGasto((atual) => ({ ...atual, data: event.target.value }))}
                      />
                    </label>
                    <button className="legacy-action-button green" type="submit" disabled={gastoSalvando}>
                      <Plus size={15} />
                      {gastoSalvando ? 'Salvando...' : 'Adicionar gasto'}
                    </button>
                  </div>
                  {gastoErro && <p className="expense-error">{gastoErro}</p>}
                  </form>
                </ReportSection>

                {gastosLoading && <div className="legacy-message">Carregando gastos...</div>}
                {gastosError && <div className="legacy-message error">{gastosError}</div>}

                {!gastosLoading && !gastosError && (
                  <ReportSection
                    title="Lançamentos do período"
                    description={`${gastos.length} ${gastos.length === 1 ? 'item registrado' : 'itens registrados'}.`}
                  >
                    {gastos.length === 0 && (
                      <ReportEmptyState
                        icon={WalletCards}
                        title="Nenhum gasto lançado"
                        description="Use o formulário acima para registrar o primeiro gasto deste período."
                      />
                    )}
                    {gastos.length > 0 && (
                      <div className="expense-list">
                        {gastos
                          .slice()
                          .sort((a, b) => a.descricao.localeCompare(b.descricao, 'pt-BR'))
                          .map((gasto) => (
                          <article className="expense-row" key={gasto.id}>
                            <div className="expense-main">
                              <strong>{gasto.descricao}</strong>
                              <small>
                                {gasto.categoria}
                                {gasto.data ? ` • ${formatarDataCurta(gasto.data)}` : ''}
                              </small>
                            </div>
                            <div className="expense-numbers">
                              <small>{gasto.quantidade} x {formatCurrency(gasto.valorUnitario)}</small>
                              <strong>{formatCurrency(totalDoGasto(gasto))}</strong>
                            </div>
                            <button
                              className="expense-delete"
                              type="button"
                              title="Excluir gasto"
                              onClick={() => excluirGasto(gasto)}
                            >
                              <Trash2 size={16} />
                            </button>
                          </article>
                        ))}
                      </div>
                    )}
                  </ReportSection>
                )}
              </>
            )}

            {activeTab === 'lista' && (
              <>
                <style>{`@media print { @page { size: A4 portrait !important; } }`}</style>
                <ReportHeader
                  title="Lista de inscrições"
                  description={`${listaInscricoes.length} ${listaInscricoes.length === 1 ? 'inscrição' : 'inscrições'} no período, em ordem de cadastro.`}
                  eyebrow="Documentos"
                  actions={(
                    <button className="legacy-action-button green" type="button" onClick={printCurrentReport}>
                      <Printer size={15} />
                      Imprimir lista
                    </button>
                  )}
                />

                <div className="lista-inscricao-wrapper">
                  <table className="lista-inscricao-table">
                    <thead>
                      <tr>
                        <th className="lista-col-num">#</th>
                        <th className="lista-col-foto">Foto</th>
                        <th className="lista-col-nome">Nome</th>
                        <th className="lista-col-turma">Turma</th>
                        <th className="lista-col-camisa">Tamanho</th>
                        <th className="lista-col-data">Inscrição</th>
                      </tr>
                    </thead>
                    <tbody>
                      {listaInscricoes.map((item, index) => (
                        <tr key={item.id || item.nomeCompleto} className="lista-inscricao-row">
                          <td className="lista-col-num lista-num">{index + 1}</td>
                          <td className="lista-col-foto">
                            <div className="lista-foto-circle">
                              {item.fotoUrl
                                ? <img src={item.fotoUrl} alt={item.nomeCompleto} />
                                : <span>{item.nomeCompleto.slice(0, 2).toUpperCase()}</span>
                              }
                            </div>
                          </td>
                          <td className="lista-col-nome lista-nome">{item.nomeCompleto}</td>
                          <td className="lista-col-turma">{item.turma || 'Sem turma'}</td>
                          <td className="lista-col-camisa lista-center">{item.tamanhoCamisa || '-'}</td>
                          <td className="lista-col-data lista-center">{formatarDataInscricao(item)}</td>
                        </tr>
                      ))}
                      {listaInscricoes.length === 0 && (
                        <tr>
                          <td colSpan={6} className="lista-vazio">Nenhuma inscrição neste período.</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'carteirinhas' && (
              <>
                <ReportHeader
                  title="Carteirinhas"
                  description="Prepare e gere as identificações das crianças inscritas neste período."
                  eyebrow="Documentos"
                />
                <ReportSection
                  title="Gerador de carteirinhas"
                  description="Selecione as opções de impressão e confira o resultado antes de gerar o arquivo."
                >
                  <CarteirinhasPanel inscricoes={yearInscricoes} />
                </ReportSection>
              </>
            )}


            {activeTab === 'final' && (
              <>
                <ReportHeader
                  title="Relatório final"
                  description="Documento executivo consolidado para administração e presidência."
                  eyebrow="Documento consolidado"
                  actions={(
                    <button
                      className="legacy-action-button green"
                      type="button"
                      onClick={gerarRelatorioFinal}
                      disabled={gerandoRelatorioFinal || loading || gastosLoading || chamadasCarregando || comprasEventoCarregando}
                    >
                      {gerandoRelatorioFinal ? <LoaderCircle className="spin" size={15} /> : <FileText size={15} />}
                      {gerandoRelatorioFinal ? 'Gerando PDF...' : 'Gerar PDF completo'}
                    </button>
                  )}
                />

                <ReportMetrics items={[
                  { label: 'Inscrições', value: yearInscricoes.length, icon: UsersRound },
                  { label: 'Chamadas', value: chamadasDoPeriodo.length, tone: 'info', icon: ClipboardCheck },
                  { label: 'Receita', value: formatCurrency(receita), tone: 'positive', icon: WalletCards },
                  { label: 'Gastos', value: formatCurrency(totalGastos), tone: 'negative', icon: Receipt }
                ]} />

                {(chamadasCarregando || comprasEventoCarregando || gastosLoading) && (
                  <div className="legacy-message">Carregando dados para o relatorio final...</div>
                )}

                <div className="report-grid report-grid--guidance">
                  <article className="report-card">
                    <h3>Conteúdo consolidado</h3>
                    <p className="report-muted">Resumo executivo, financeiro, inscrições por turma, camisas, presenças, gastos e adicionais.</p>
                  </article>
                  <article className="report-card">
                    <h3>Regra de presença</h3>
                    <p className="report-muted">Se apenas a saída foi marcada, a criança também entra como presente.</p>
                  </article>
                  <article className="report-card">
                    <h3>Pendências destacadas</h3>
                    <p className="report-muted">Pagamentos, termos, adicionais e casos de saída sem entrada são destacados.</p>
                  </article>
                </div>
              </>
            )}

            {activeTab === 'termo' && (
              <>
                <ReportHeader
                  title="Termos de autorização"
                  description="Acompanhe assinaturas e abra os dados completos enviados pelos responsáveis."
                  eyebrow="Documentos"
                />
                {termosCarregando && <p className="report-muted">Carregando termos...</p>}
                {termosErro && <div className="legacy-message error">{termosErro}</div>}
                {!termosCarregando && !termosErro && (
                  <>
                    <ReportMetrics items={[
                      { label: 'Inscrições', value: yearInscricoes.length, icon: UsersRound },
                      { label: 'Assinados', value: inscricoesComTermo.length, tone: 'positive', icon: CheckCheck },
                      { label: 'Pendentes', value: inscricoesSemTermo.length, tone: inscricoesSemTermo.length ? 'warning' : 'positive', icon: FileText }
                    ]} />
                    <div className="report-grid">
                      <article className="report-card">
                        <h3>Já assinaram</h3>
                        <strong>{inscricoesComTermo.length}</strong>
                        <small className="report-muted">Clique em um nome para ver os dados preenchidos.</small>
                        {renderTermosAssinadosList(inscricoesComTermo)}
                      </article>
                      <article className="report-card">
                        <h3>Faltam assinar</h3>
                        <strong>{inscricoesSemTermo.length}</strong>
                        {renderNameList(inscricoesSemTermo)}
                      </article>
                    </div>
                  </>
                )}
              </>
            )}
          </ReportPanel>
        )}
            </div>
          </div>
        </div>

        {termoSelecionado && (
          <div className="admin-floating-backdrop" role="presentation" onClick={() => setTermoSelecionado(null)}>
            <div
              className="admin-floating-panel wide"
              role="dialog"
              aria-modal="true"
              aria-label="Termo de Autorização"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="admin-floating-header">
                <h3>{termoSelecionado.nomeCrianca}</h3>
                <button type="button" onClick={() => setTermoSelecionado(null)} aria-label="Fechar">
                  <X size={18} />
                </button>
              </div>
              <div className="termo-detalhe">
                <p><strong>Turma:</strong> {termoSelecionado.turma || '-'}</p>
                <p><strong>Responsável:</strong> {termoSelecionado.nomeResponsavel} (CPF {termoSelecionado.cpfResponsavel})</p>
                <p><strong>Telefone:</strong> {termoSelecionado.telefoneResponsavel || '-'}</p>
                <p><strong>Telefone de emergência:</strong> {termoSelecionado.telefoneEmergencia || '-'}</p>
                <p><strong>Alergias:</strong> {termoSelecionado.alergias || 'Nenhuma informada'}</p>
                <p><strong>Medicamentos de uso contínuo:</strong> {termoSelecionado.medicamentos || 'Nenhum informado'}</p>
                <p><strong>Restrições alimentares:</strong> {termoSelecionado.restricoesAlimentares || 'Nenhuma informada'}</p>
                <p><strong>Outras informações:</strong> {termoSelecionado.outrasInformacoes || '-'}</p>
                <p><strong>Convênio médico:</strong> {termoSelecionado.convenioMedico || '-'}</p>
                <p><strong>Número da carteirinha:</strong> {termoSelecionado.numeroCarteirinha || '-'}</p>
                <p><strong>Autorização de uso de imagem:</strong> {termoSelecionado.autorizaImagem ? 'Autoriza' : 'Não autoriza'}</p>
                <p><strong>Pessoas autorizadas a retirar a criança:</strong></p>
                <ul>
                  {termoSelecionado.pessoasRetirada.map((pessoa, index) => (
                    <li key={index}>
                      {pessoa.nome}
                      {pessoa.cpf ? ` - CPF ${pessoa.cpf}` : ''}
                    </li>
                  ))}
                </ul>
                {termoSelecionado.assinaturaUrl && (
                  <div className="termo-assinatura-preview">
                    <strong>Assinatura</strong>
                    <img src={termoSelecionado.assinaturaUrl} alt="Assinatura do responsável" />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RelatoriosColonia;
